import {Trans} from '@lingui/macro';
import React, {FC, useEffect, useState} from 'react';
import styled from 'styled-components';

import {Handler, HandlerOf} from '../../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroup} from '../../../../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroup';
import {fontSizes, fontWeights} from '../../../../../../../libs/shared/ui/src/styles/fontStyles';
import {useAppDispatch, useAppSelector} from '../../../../../../helpers/reduxHelpers';
import {
  IntegrationSettingsTypesEnum,
  JiraIntegrationSettingsModel,
} from '../../../../../../models/api/integrationModel';
import {
  generateConfigurationKeysForIntegrationAsyncAction,
  revokeConfigurationKeysForIntegrationAsyncAction,
} from '../../../../../../store/data/integrations/integrationsActions';
import {getIsUserGlobalAdmin} from '../../../../../../store/data/teammates/teammatesSelectors';
import {maybeRenderConfigureDialogTextBox} from '../shared/settingsConfigureHelpers';

/*
 * Props.
 */

interface SettingsAppsConfigureJiraSelfHostedProps {
  configurationSettings?: JiraIntegrationSettingsModel;
  setConfigurationSettings: HandlerOf<JiraIntegrationSettingsModel | undefined>;
}

/*
 * Style.
 */

const StyledButtonGroup = styled(ButtonGroup)`
  padding: 0 58px;
  margin: 8px 0;
`;

const StyledGenerateKeysButton = styled(Button)`
  margin-right: 16px;
`;

const StyledPublicKeyTitleDiv = styled.div`
  font-weight: ${fontWeights.semibold};
  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.small};
`;

const StyledPre = styled.pre`
  background: ${(p) => p.theme.greys.shade20};
  border-radius: 8px;
  padding: 8px;
  font-family: monospace;
  white-space: pre-wrap;
  overflow: auto;
  margin-top: 8px;
  font-size: ${fontSizes.verySmall};
  min-height: 30px;
`;

const StyledTextWrapperDiv = styled.div`
  margin-bottom: 16px;
  font-size: ${fontSizes.verySmall};
  color: ${(p) => p.theme.greys.shade60};
  line-height: 16px;
`;

/*
 * Component.
 */

export const SettingsAppsConfigureJiraSelfHosted: FC<SettingsAppsConfigureJiraSelfHostedProps> = (props) => {
  const {configurationSettings, setConfigurationSettings} = props;
  const isGlobalAdmin = useAppSelector(getIsUserGlobalAdmin);
  const isSelfHosted = Boolean(configurationSettings?.jiraServer);
  const dispatch = useAppDispatch();

  const [jiraServerUrl, setJiraServerUrl] = useState(configurationSettings?.jiraServerUrl || '');

  // When keys are generated/revoked it pushes a new update to the integration model, this will watch the local
  // value and make sure it is not cleared when that happens.
  useEffect(() => {
    if (configurationSettings?.jiraServerUrl !== jiraServerUrl) {
      setConfigurationSettings({
        ...configurationSettings,
        jiraServerUrl,
      });
    }
  }, [isSelfHosted, setConfigurationSettings, configurationSettings, jiraServerUrl]);

  const onGenerateKeysClick = () => {
    dispatch(generateConfigurationKeysForIntegrationAsyncAction(IntegrationSettingsTypesEnum.JIRA));
  };

  const onRevokeKeysClick = () => {
    dispatch(revokeConfigurationKeysForIntegrationAsyncAction(IntegrationSettingsTypesEnum.JIRA));
  };

  if (!isSelfHosted) {
    return null;
  }
  return (
    <>
      {maybeRenderConfigureDialogTextBox(
        jiraServerUrl,
        <Trans id="settings-apps-configure-app-dialog-jira-server-url">JIRA Server URL</Trans>,
        (value) => {
          setJiraServerUrl(value.trim());
          setConfigurationSettings({
            ...configurationSettings,
            jiraServerUrl: value.trim(),
          });
        },
        {
          isDisabled: !isGlobalAdmin,
          isRequired: true,
        },
      )}
      {/* Hard-coded */}
      {maybeRenderConfigureDialogTextBox(
        'Front',
        <Trans id="settings-apps-configure-app-dialog-jira-consumer-name">Consumer Name</Trans>,
        () => {},
        {
          isReadOnly: true,
        },
      )}
      {maybeRenderConfigureDialogTextBox(
        configurationSettings?.readonly?.consumerKey || '',
        <Trans id="settings-apps-configure-app-dialog-jira-consumer-key">Consumer Key</Trans>,
        () => {},
        {
          isReadOnly: true,
        },
      )}
      {maybeRenderPublicKey(configurationSettings, isGlobalAdmin)}
      {renderKeyButtons(configurationSettings, isGlobalAdmin, onGenerateKeysClick, onRevokeKeysClick)}
    </>
  );
};

/*
 * Helpers.
 */

function renderKeyButtons(
  configurationSettings: JiraIntegrationSettingsModel | undefined,
  isGlobalAdmin: boolean,
  onGenerateKeysClick: Handler,
  onRevokeKeysClick: Handler,
) {
  const hasPublicKey = Boolean(configurationSettings?.readonly?.publicKey);

  return (
    <>
      <StyledButtonGroup>
        <StyledGenerateKeysButton
          variant={ButtonVariantsEnum.PRIMARY}
          isDisabled={Boolean(hasPublicKey || !configurationSettings?.jiraServerUrl) || !isGlobalAdmin}
          onClick={onGenerateKeysClick}
        >
          <Trans id="settings-apps-configure-app-dialog-jira-generate-keys">Generate Keys</Trans>
        </StyledGenerateKeysButton>
        <Button
          variant={ButtonVariantsEnum.DANGER}
          isDisabled={!hasPublicKey || !isGlobalAdmin}
          onClick={onRevokeKeysClick}
        >
          <Trans id="settings-apps-configure-app-dialog-jira-revoke-keys">Revoke Keys</Trans>
        </Button>
      </StyledButtonGroup>
      {hasPublicKey && (
        <StyledTextWrapperDiv>
          <Trans id="settings-apps-configure-app-dialog-jira-revoke-keys-disclaimer">
            If a user is currently signed in, revoking the public key will completely sign out this user along
            with all teammates using OAuth sign in for the self-hosted Jira server.
          </Trans>
        </StyledTextWrapperDiv>
      )}
    </>
  );
}

function maybeRenderPublicKey(
  configurationSettings: JiraIntegrationSettingsModel | undefined,
  isGlobalAdmin: boolean,
) {
  if (!isGlobalAdmin) {
    return null;
  }
  return (
    <>
      <StyledPublicKeyTitleDiv>
        <Trans id="settings-apps-configure-app-dialog-jira-public-key">Public key</Trans>
      </StyledPublicKeyTitleDiv>
      <StyledPre>{configurationSettings?.readonly?.publicKey || ''}</StyledPre>
    </>
  );
}
